/**
 * Tone-Mapping Operators — collapse linear HDR (≥12 stops) to display.
 *
 * Pure functions over Float32 RGBA buffers. Each operator returns a new
 * buffer with values clamped to [0, 1] — ready for sRGB encoding by the
 * existing display pipeline.
 *
 * Reference:
 *   - Reinhard et al. 2002 — global luminance compression.
 *   - Drago et al. 2003 — adaptive logarithmic mapping.
 *   - Hable 2010 (Uncharted 2 filmic curve).
 *   - ACES 1.0 fitted polynomial (Narkowicz 2015) — Arri-look-alike.
 *
 * Status: Phase 2A skeleton — Reinhard wired since it's a 1-pass; the
 *   filmic + ACES variants have parameter tables that need a stand-alone
 *   review pass before shipping.
 */

const REC709 = { r: 0.2126, g: 0.7152, b: 0.0722 };
const EPS = 1e-12;

function luminance(r, g, b) { return REC709.r * r + REC709.g * g + REC709.b * b; }

/**
 * Reinhard global tone-map: L' = L / (1 + L). Optional white-point
 * extension (`Lwhite`) lets very bright highlights wrap to 1.0 cleanly
 * instead of asymptotically.
 *
 * @param {Float32Array} rgba       linear scene-referred [0, ∞)
 * @param {object} [opts]
 *   @prop {number} [Lwhite=4]      luminance that maps to 1.0
 *   @prop {number} [exposure=1]    pre-multiplier in linear light
 * @returns {Float32Array}          new buffer, clamped to [0, 1]
 */
export function reinhard(rgba, opts = {}) {
    const Lwhite = opts.Lwhite != null ? opts.Lwhite : 4;
    const exp    = opts.exposure != null ? opts.exposure : 1;
    const Lw2    = Lwhite * Lwhite;
    const out    = new Float32Array(rgba.length);
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i] * exp, g = rgba[i + 1] * exp, b = rgba[i + 2] * exp;
        const L = luminance(r, g, b);
        if (L < EPS) {
            out[i] = 0; out[i + 1] = 0; out[i + 2] = 0; out[i + 3] = rgba[i + 3];
            continue;
        }
        const Lprime = (L * (1 + L / Lw2)) / (1 + L);
        const k = Lprime / L;
        out[i]     = Math.min(1, r * k);
        out[i + 1] = Math.min(1, g * k);
        out[i + 2] = Math.min(1, b * k);
        out[i + 3] = rgba[i + 3];
    }
    return out;
}

/**
 * Hable filmic curve (Uncharted 2). Single-curve, no params other than
 * exposure and a global desaturation pass. Closer to Arri Log-C in feel.
 */
export function hable(_rgba, _opts = {}) {
    throw new Error('tone-map.hable: not implemented (Phase 2A)');
}

/**
 * Narkowicz ACES-fitted polynomial — the cinematic-look-alike. Will be
 * the default for the "Arri-grade" preset once wired.
 */
export function acesFitted(_rgba, _opts = {}) {
    throw new Error('tone-map.acesFitted: not implemented (Phase 2A)');
}
